import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, RouterProvider, createBrowserRouter } from "react-router";

import { ActingTenantProvider } from "./lib/actingTenant";
import { AuthProvider, useAuth } from "./lib/auth";
import { useRoles, type StaffRole } from "./lib/roles";
import { Shell } from "./shell/AdminShell";
import { Tenants } from "./features/tenants/Tenants";
import { TenantDetail } from "./features/tenants/TenantDetail";
import { Staff } from "./features/staff/Staff";
import { ClientDetail } from "./features/clients/ClientDetail";
import { Clients } from "./features/clients/Clients";
import { AcceptInvite } from "./features/staff/AcceptInvite";
import { Templates } from "./features/dossiers/Templates";
import { Active } from "./features/dossiers/Active";
import { Forms } from "./features/forms/Forms";
import { FormDetail } from "./features/forms/FormDetail";
import { FormTemplates } from "./features/forms/FormTemplates";
import { MyBrokerage } from "./features/tenants/MyBrokerage";
import { MyRateCard } from "./features/staff/RateCard";
import { Requests } from "./features/requests/Requests";
import { Appointments } from "./features/appointments/Appointments";
import { DashboardHome } from "./features/DashboardHome";
import { Profile } from "./features/Profile";
import { MyUsage } from "./features/MyUsage";
import { DocumentReview } from "./features/DocumentReview";
import { BillingPlans } from "./features/billing/BillingPlans";
import { Margin } from "./features/billing/Margin";
import { Subscriptions } from "./features/billing/Subscriptions";
import { AgentUsage } from "./features/billing/AgentUsage";
import { InvoiceTrigger } from "./features/billing/InvoiceTrigger";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 2, refetchOnWindowFocus: false },
  },
});

/**
 * Redirect a staff member to the Dashboard if their role can't reach this route
 * (the backend would 403 anyway). When the role is unknown — dev with no token,
 * or a token missing the claim — `allows` returns true so we don't dead-end.
 * The `allow` lists mirror the nav gates in Shell.tsx and the backend
 * require_role gates.
 */
function RequireRole({
  allow,
  children,
}: {
  allow: StaffRole[];
  children: React.ReactNode;
}) {
  const { allows } = useRoles();
  if (!allows(allow)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    element: <Shell />,
    children: [
      { path: "/", element: <DashboardHome /> },
      { path: "/profile", element: <Profile /> },
      {
        path: "/document-review",
        element: (
          <RequireRole allow={["super_admin", "brokerage_admin", "agent"]}>
            <DocumentReview />
          </RequireRole>
        ),
      },
      {
        path: "/my-usage",
        element: (
          <RequireRole allow={["agent"]}>
            <MyUsage />
          </RequireRole>
        ),
      },
      {
        path: "/tenants",
        element: (
          <RequireRole allow={["super_admin"]}>
            <Tenants />
          </RequireRole>
        ),
      },
      {
        path: "/tenants/:tenantId",
        element: (
          <RequireRole allow={["super_admin"]}>
            <TenantDetail />
          </RequireRole>
        ),
      },
      {
        path: "/staff",
        element: (
          <RequireRole allow={["super_admin", "brokerage_admin"]}>
            <Staff />
          </RequireRole>
        ),
      },
      {
        path: "/clients",
        element: (
          <RequireRole allow={["super_admin", "brokerage_admin", "agent"]}>
            <Clients />
          </RequireRole>
        ),
      },
      {
        // Agents reach their own clients here too — the server scopes the
        // record, so the route gate matches the list's.
        path: "/clients/:clientId",
        element: (
          <RequireRole allow={["super_admin", "brokerage_admin", "agent"]}>
            <ClientDetail />
          </RequireRole>
        ),
      },
      {
        path: "/requests",
        element: (
          <RequireRole allow={["super_admin", "brokerage_admin", "agent"]}>
            <Requests />
          </RequireRole>
        ),
      },
      {
        path: "/appointments",
        element: (
          <RequireRole allow={["super_admin", "brokerage_admin", "agent"]}>
            <Appointments />
          </RequireRole>
        ),
      },
      {
        path: "/dossiers/templates",
        element: (
          <RequireRole allow={["super_admin", "brokerage_admin"]}>
            <Templates />
          </RequireRole>
        ),
      },
      {
        path: "/dossiers/active",
        element: (
          <RequireRole allow={["brokerage_admin", "agent"]}>
            <Active />
          </RequireRole>
        ),
      },
      {
        path: "/forms",
        element: (
          <RequireRole allow={["brokerage_admin", "agent"]}>
            <Forms />
          </RequireRole>
        ),
      },
      {
        // Static, so it must not be read as /forms/:formId. React Router ranks
        // static segments above dynamic ones, but the order says the intent.
        path: "/forms/templates",
        element: (
          // V073 — agents with their own zipForm licence upload and map their
          // OWN templates here. The server scopes every row to its owner, so an
          // agent sees the catalog plus their own and can edit only the latter.
          //
          // super_admin belongs here too: a template they upload IS the
          // platform-standard row (trec-svc's _owning_tenant_id returns NULL for
          // them), and no other role may map one. See the nav entry in
          // AdminShell for the full reasoning.
          <RequireRole allow={["super_admin", "brokerage_admin", "agent"]}>
            <FormTemplates />
          </RequireRole>
        ),
      },
      {
        path: "/forms/:formId",
        element: (
          <RequireRole allow={["super_admin", "brokerage_admin", "agent"]}>
            <FormDetail />
          </RequireRole>
        ),
      },
      {
        // The brokerage_admin's own brokerage (folded-in "Brokerage settings";
        // a super_admin edits any tenant's via /tenants/:tenantId instead).
        path: "/brokerage",
        element: (
          // Agents included, read-only — see the nav entry in AdminShell. The
          // backend splits it the same way: brokerage-settings lets an agent GET
          // and refuses their PATCH.
          <RequireRole allow={["super_admin", "brokerage_admin", "agent"]}>
            <MyBrokerage />
          </RequireRole>
        ),
      },
      {
        // The agent's own rate card; admins edit an agent's from Staff → Manage.
        path: "/rate-card",
        element: (
          <RequireRole allow={["agent"]}>
            <MyRateCard />
          </RequireRole>
        ),
      },
      {
        // The cost drill-down is now a level INSIDE Margin — it read the same
        // rollups and could never say whether a brokerage was worth serving,
        // because it showed no revenue. Kept as a redirect so an old bookmark
        // lands on the screen that absorbed it rather than erroring.
        path: "/billing/costs",
        element: <Navigate to="/billing/margin" replace />,
      },
      {
        path: "/billing/plans",
        element: (
          <RequireRole allow={["super_admin"]}>
            <BillingPlans />
          </RequireRole>
        ),
      },
      {
        path: "/billing/invoices",
        element: (
          <RequireRole allow={["super_admin"]}>
            <InvoiceTrigger />
          </RequireRole>
        ),
      },
      {
        path: "/billing/margin",
        element: (
          <RequireRole allow={["super_admin"]}>
            <Margin />
          </RequireRole>
        ),
      },
      {
        path: "/billing/subscriptions",
        element: (
          <RequireRole allow={["super_admin"]}>
            <Subscriptions />
          </RequireRole>
        ),
      },
      {
        path: "/billing/agent-usage",
        element: (
          <RequireRole allow={["super_admin", "brokerage_admin"]}>
            <AgentUsage />
          </RequireRole>
        ),
      },
    ],
  },
]);

export function App() {
  // The staff-invite accept page is public — the invitee has no account yet, so
  // it renders OUTSIDE the SSO AuthProvider/AuthGate (just needs the QueryClient
  // + the anonymous api client).
  if (window.location.pathname.startsWith("/accept-invite")) {
    return (
      <QueryClientProvider client={queryClient}>
        <AcceptInvite />
      </QueryClientProvider>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/* Inside AuthProvider (useRoles reads both) and OUTSIDE the router, so
            switching brokerage re-renders every screen without remounting the
            router or reloading the page. */}
        <ActingTenantProvider>
          <AuthGate>
            <RouterProvider router={router} />
          </AuthGate>
        </ActingTenantProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

/**
 * Renders the app only once auth resolves. In Auth0 (SSO) mode an
 * unauthenticated visitor gets the login screen; in dev mode (no Auth0 env) the
 * app renders straight through and the sidebar token-paste is the auth path.
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const { ready, mode, isAuthenticated, login } = useAuth();
  if (!ready) return <CenteredMessage>Loading…</CenteredMessage>;
  if (mode === "auth0" && !isAuthenticated) {
    return (
      <CenteredMessage>
        <h1 style={{ marginBottom: 8 }}>My Home Journey · admin</h1>
        <p style={{ color: "var(--rp-color-text-muted)", marginBottom: 20 }}>
          Staff sign-in
        </p>
        <button
          type="button"
          className="rp-btn rp-btn--primary"
          onClick={login}
        >
          Sign in with SSO
        </button>
      </CenteredMessage>
    );
  }
  return <>{children}</>;
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}
